<!--
Author: Alexey Usov (dax@xdax.ru, https://t.me/doubleaxe, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
-->
<script setup lang="ts">
import type {LinkModel} from '@/scripts/model/store';
import {anchorSideFor, LinkShapeModelBuilder} from '@/scripts/model/link-shape';
import {injectSettings} from '@/scripts/settings';
import {computed} from 'vue';

const props = defineProps<{
    link?: LinkModel;
    color?: string;
}>();

const settings = injectSettings();
const buildLink = computed(() => {
    const link0 = props.link;
    const svgLink = link0?.buildShape()?.svgLink;
    return svgLink || '';
});

const colorClass = computed(() => {
    const defaultColor = 'link-stroke-black';
    if(props.color)
        return props.color;
    if(settings.colorfulLinks)
        return props.link?.colorClass || defaultColor;
    return defaultColor;
});

//fork addition: directional arrowhead at the consuming (input) end of the
//link, so flow direction is readable at a glance. The tip sits exactly on
//the input port's anchor point (same geometry as LinkShapeModelBuilder)
//and points into the card; the anchor side comes from the owner item's
//flow orientation, so it is correct for LR/RL and for the top/bottom
//ports of TB/BT items alike.
const arrowPoints = computed(() => {
    const inputIo = props.link?.input;
    const rect = inputIo?.rect;
    if(!inputIo || !rect?.width)
        return '';
    //fork change: the tip sits on the input port's anchor point (the
    //same geometry the link shape builder uses) and the barbs trail
    //back along the edge's outward normal — correct for left/right
    //ports and for the top/bottom ports of TB/BT orientations alike
    const side = anchorSideFor(true, inputIo.ownerItem?.orientation, inputIo.isFlipped);
    const tip = LinkShapeModelBuilder.calculateAnchorPoint(rect, side);
    const normal = LinkShapeModelBuilder.anchorNormal(side);
    const length = settings.iconSize * 0.9;
    const half = settings.iconSize * 0.42;
    const barbX = tip.x + (normal.x * length);
    const barbY = tip.y + (normal.y * length);
    //perpendicular of the normal
    const perpX = normal.y;
    const perpY = normal.x;
    return `${tip.x},${tip.y} ${barbX + (perpX * half)},${barbY + (perpY * half)} ${barbX - (perpX * half)},${barbY - (perpY * half)}`;
});
const fillClass = computed(() => colorClass.value.replace('link-stroke-', 'link-fill-'));
</script>

<template>
    <path
        :d="buildLink"
        fill="none"
        :stroke-width="settings.iconSize"
        class="link"
        :class="colorClass"
    />
    <polygon
        v-if="arrowPoints"
        :points="arrowPoints"
        class="link-arrow"
        :class="fillClass"
    />
</template>

<style scoped>
.link:hover {
    opacity: 0.7;
}
/* fork addition: arrowhead must never swallow clicks meant for the link */
.link-arrow {
    pointer-events: none;
}
</style>
